import React from "react";

import PropTypes from "prop-types";
import { FormattedMessage, injectIntl, intlShape } from "react-intl";
import { connect } from "react-redux";

import { Divider } from "@ps-design-system/components/Divider";
import { Text } from "@ps-design-system/components/Text";

import Payments from "../../../../api/api-payments";
import { managedAccountSuccess } from "../../app-main/actions/nav";

import {
  TAX_ID_VARIANTS,
  TAX_INFORMATION_STATUSES,
  TAX_INFORMATION_TYPES,
} from "./tax-information.constants";
import {
  getTaxInformationMetaById,
  removeNonAlphanumeric,
} from "./tax-information.utils";
import { TaxNumberForm, VatNumberForm } from "./TaxInformationForms";
import { TaxInformationStatusBanner } from "./TaxInformationStatusBanner/TaxInformationStatusBanner";

const TaxInformationSection = ({
  taxId,
  vatId,
  taxStatus,
  vatStatus,
  uploadTaxInformation,
  intl,
}) => {
  const [taxSubmitError, setTaxSubmitError] = React.useState(null);
  const [vatSubmitError, setVatSubmitError] = React.useState(null);

  const handleSubmitTaxNumber = async ({ taxInformation }) => {
    setTaxSubmitError(null);
    try {
      await uploadTaxInformation({
        taxNumber: removeNonAlphanumeric(taxInformation),
      });
    } catch (error) {
      setTaxSubmitError(error.message);
    }
  };

  const handleSubmitVatNumber = async ({ taxInformation }) => {
    setVatSubmitError(null);
    try {
      await uploadTaxInformation({
        vatNumber: removeNonAlphanumeric(taxInformation),
      });
    } catch (error) {
      setVatSubmitError(error.message);
    }
  };

  const renderSection = (type = "tax") => {
    const taxInformationId = "tax" === type ? taxId : vatId;
    const taxInformationStatus = "tax" === type ? taxStatus : vatStatus;

    const {
      format,
      formatStringMessageDescriptor,
      fullNameMessageDescriptor,
      shortNameMessageDescriptor,
    } = getTaxInformationMetaById(taxInformationId);

    return (
      <>
        <Text typeface="headline-1-m">
          <FormattedMessage {...fullNameMessageDescriptor} />
        </Text>
        <TaxInformationStatusBanner
          taxInformationId={taxInformationId}
          taxInformationStatus={taxInformationStatus}
        />
        {"tax" === type ? (
          <TaxNumberForm
            onSubmit={handleSubmitTaxNumber}
            submitError={taxSubmitError}
            setSubmitError={setTaxSubmitError}
            taxInformationId={taxId}
            intl={intl}
          />
        ) : (
          <VatNumberForm
            onSubmit={handleSubmitVatNumber}
            submitError={vatSubmitError}
            setSubmitError={setVatSubmitError}
            taxInformationId={vatId}
            intl={intl}
          />
        )}
        <Text typeface="caption-2" className="tw-text-gray-600">
          <FormattedMessage
            id="hosting.tax_information_section.section_note"
            defaultMessage="Your {taxIdShortName} is used for tax reporting purposes and will be securely transmitted to Stripe. Enter your {taxIdFormatString} federal tax identification number in the format {taxIdFormat}."
            values={{
              taxIdShortName: (
                <FormattedMessage {...shortNameMessageDescriptor} />
              ),
              taxIdFormatString: (
                <FormattedMessage {...formatStringMessageDescriptor} />
              ),
              taxIdFormat: format,
            }}
          />
        </Text>
      </>
    );
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-16">
      {taxId ? renderSection("tax") : null}
      {taxId && vatId ? <Divider spacing={8} /> : null}
      {vatId ? renderSection("vat") : null}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  uploadTaxInformation: async ({ taxNumber, vatNumber }) => {
    return dispatch(async (dispatch, getState) => {
      const { auth, profile, nav } = getState();
      const accessToken = auth.accessToken;
      const SSOid = profile.SSOid;
      const variant = nav?.managedAccount?.business_type;

      return new Promise((resolve, reject) => {
        const payload = {};
        if (variant === TAX_ID_VARIANTS.INDIVIDUAL) {
          if (taxNumber) {
            payload.individual = {
              id_number: taxNumber,
            };
          }
        } else {
          if (taxNumber) {
            payload.company = {
              tax_id: taxNumber,
            };
          }
          if (vatNumber) {
            payload.company = {
              vat_id: vatNumber,
            };
          }
        }
        Payments.saveManagedAccountData(
          SSOid,
          accessToken,
          payload,
          true,
          (response) => {
            if (response.error || 200 !== response.statusCode) {
              const errorMessage =
                "string" === typeof response?.error
                  ? response?.error
                  : "string" === typeof response?.error?.raw?.message
                    ? response?.error?.raw?.message
                    : "Something went wrong";
              reject(new Error(errorMessage));
            }
            dispatch(managedAccountSuccess(response));
            resolve(response.data);
          },
        );
      });
    });
  },
});

TaxInformationSection.propTypes = {
  taxId: PropTypes.oneOf(Object.values(TAX_INFORMATION_TYPES)),
  vatId: PropTypes.oneOf(Object.values(TAX_INFORMATION_TYPES)),
  taxStatus: PropTypes.oneOf(Object.values(TAX_INFORMATION_STATUSES)),
  vatStatus: PropTypes.oneOf(Object.values(TAX_INFORMATION_STATUSES)),
  uploadTaxInformation: PropTypes.func,
  intl: intlShape,
};

const WrappedTaxInformationSection = connect(
  null,
  mapDispatchToProps,
)(injectIntl(TaxInformationSection));

export { WrappedTaxInformationSection as TaxInformationSection };
